import assert from 'assert';
import type { LDContext } from 'launchdarkly-react-client-sdk';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useInsertionEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import { ApolloProvider } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Toaster } from '@savvy-kit/components/sonner';
import { SpeedInsights } from '@vercel/speed-insights/next';
import { ThemeProvider as NextThemesProvider, useTheme as useNextTheme } from 'next-themes';
import nProgress from 'nprogress';
import { NuqsAdapter } from 'nuqs/adapters/next';
import { Toaster as ReactHotToaster } from 'react-hot-toast';
import { AdvisorDashNavProvider } from 'src/components/advisor-dashboard/layout/advisor-dash-nav-context';
import { SplashScreen } from 'src/components/loading-and-splash/splash-screen';
import { LaunchDarklyFlagsWithStatusProvider } from 'src/contexts/launch-darkly-flags-with-status-provider';
import { UserProfileProvider } from 'src/contexts/user-profile-context';
import { ConfirmationDialogProvider } from 'src/hooks/use-confirmation-dialog';
import { shouldReportSpeedInsightsEvent } from 'src/lib/observability/login-speed-insights';
import { SkewRefreshBanner } from 'src/lib/skew-refresh/skew-refresh-banner';
import { IGNORED_ERRORS } from 'src/utils/error';
import { RUM_VERSION } from 'src/utils/rumVersion';
import { trpc } from 'src/utils/trpc';

import type { JWTContextUser } from '@savvy/deprecated-shared/api/user-api';
import { AuthProvider } from '@savvy/deprecated-shared/contexts/jwt-context';
import { AuthConsumer } from '@savvy/deprecated-shared/contexts/jwt-context.context';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { createApolloClient } from '@savvy/deprecated-shared/utils/apollo-client';
import { isEmployeeEmail, isSavvyTestUserEmail } from '@savvy/deprecated-shared/utils/internal-savvy';
import { loggedInAsAdvisorID } from '@savvy/deprecated-shared/utils/savvy-auth';

import '@savvy-kit/styles/globals.css';
import '@xyflow/react/dist/style.css';

import { isNil } from 'es-toolkit';

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  readonly Component: NextPageWithLayout;
};

type EnhancedAppProps = AppPropsWithLayout;

Router.events.on('routeChangeStart', (_url, { shallow }) => {
  if (!shallow) nProgress.start();
});
Router.events.on('routeChangeError', () => nProgress.done());
Router.events.on('routeChangeComplete', () => nProgress.done());

// Dark mode is advisor-only. Client-facing routes (account-setup, dashboard,
// portfolio, etc.) and the impersonation entry point stay in light mode
// regardless of the advisor's saved preference.
const DARK_MODE_ROUTE_PREFIXES = ['/advisor', '/internal'] as const;
function isDarkModeRoute(pathname: string): boolean {
  return DARK_MODE_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * Memoizes LD context by stable user fields. A new object every parent render makes
 * `LDProvider` treat context as changed and re-run identify, which races flag streaming
 * and matches intermittent missing/wrong flags.
 */
function LaunchDarklyAppShell({
  children,
  clientSideID,
  user,
}: {
  readonly children: ReactNode;
  readonly clientSideID: string;
  readonly user: JWTContextUser | null;
}) {
  const ldContext = useMemo((): LDContext | undefined => {
    if (isNil(user)) return undefined;
    const key = user.id?.toString();
    if (isNil(key) || key === '') return undefined;
    return {
      kind: 'user',
      key,
      name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
      email: user.email,
      ...(user.primaryAdvisorEmail ? { primaryAdvisorEmail: user.primaryAdvisorEmail } : {}),
      ...(user.profileType === 'ADVISOR' && user.createdAt ? { createdAt: user.createdAt } : {}),
    };
  }, [
    user?.id,
    user?.email,
    user?.firstName,
    user?.lastName,
    user?.profileType,
    user?.createdAt,
    user?.primaryAdvisorEmail,
  ]);

  /**
   * NB: We want flag evaluations for logged-out (anonymous) visitors and a different evaluation for
   * logged-in users—e.g. a login-screen feature behind a flag we can turn off quickly.
   *
   * The React LDProvider effectively tracks one active context per mount. `deferInitialization`
   * delays creating the client until `context` exists, which rules out anonymous flags if we only
   * set context after login.
   *
   * So we init for anonymous, then use a React `key` to remount and re-initialize the provider when
   * the user signs in, so evaluations match the signed-in user.
   */

  let ldProviderKey = 'ld-anonymous';

  if (user) {
    ldProviderKey = user.id ? `ld-user-${user.id}` : 'ld-authenticated-pending-id';
  }

  return (
    <LaunchDarklyFlagsWithStatusProvider key={ldProviderKey} clientSideID={clientSideID} context={ldContext}>
      {/* The version-skew slot lives here rather than beside the two AuthConsumer branches: it
          reads the auto-reload kill switch, so it has to be under the LD provider, and one
          placement covers both the anonymous and signed-in trees. */}
      <SkewRefreshBanner />
      {children}
    </LaunchDarklyFlagsWithStatusProvider>
  );
}

function ThemeClassSync({ children }: { readonly children: ReactNode }) {
  const { resolvedTheme, forcedTheme } = useNextTheme();
  // next-themes can't resolve until localStorage is readable on the client, so
  // the effect below stays inert until mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setMounted(true);
  }, []);
  // forcedTheme (set by NextThemesProvider for client-facing routes) overrides
  // the user's saved preference. resolvedTheme always reflects the saved
  // preference, so honoring it would leak dark mode onto routes the route
  // scoping is supposed to keep light.
  const effectiveTheme = forcedTheme ?? resolvedTheme;
  // Belt-and-suspenders: mirror effectiveTheme onto <html> class. next-themes
  // already does this, but observed cases where the class is briefly absent
  // (e.g. after route changes / HMR), which leaves Tailwind tokens like
  // `text-foreground` resolving to light values on a dark route.
  // useInsertionEffect lands the class before browser paint (less flash) on the
  // client; the `mounted` gate keeps the effect inert during SSR.
  useInsertionEffect(() => {
    if (!mounted) return;
    const root = document.documentElement;
    const isDark = effectiveTheme === 'dark';
    root.classList.toggle('dark', isDark);
    root.classList.toggle('light', !isDark);
  }, [mounted, effectiveTheme]);
  return children;
}

function App(props: EnhancedAppProps) {
  const { Component } = props;
  const pageProps = props.pageProps;
  const isSmallScreen = useIsSmallScreen();
  const router = useRouter();
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout || ((page) => page);

  const [initedLibs, setInitedLibs] = useState(false);
  useEffect(() => {
    window.onerror = (err) => {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      const errorString = err.toString();
      if (IGNORED_ERRORS.some((ignoredError) => errorString.includes(ignoredError))) {
        return true;
      }
      throw new Error(errorString);
    };
    window.onstorage = (e) => {
      if (e.key === 'accessToken' && ((!e.newValue && e.oldValue) || (e.newValue && !e.oldValue))) {
        window.location.reload();
      }
    };
    if (process.env['NEXT_PUBLIC_VERCEL_ENV'] === 'production') {
      const dataDogKey = process.env['NEXT_PUBLIC_DATADOG_CLIENT_KEY'];
      assert(!isNil(dataDogKey), '');
      datadogLogs.init({
        clientToken: dataDogKey,
        env: process.env['NEXT_PUBLIC_VERCEL_ENV'],
        forwardErrorsToLogs: true,
        sampleRate: 100,
        service: 'savvy-pinetree-app',
        version: RUM_VERSION,
      });
    }
  }, []);
  const ldClientId = process.env['NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_ID'];
  assert(!isNil(ldClientId), 'NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_ID must be set in .env');
  return (
    <>
      <Head>
        <title>Savvy</title>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        <meta name="theme-color" content="#ffffff" media="(prefers-color-scheme: light)" />
        <meta name="theme-color" content="#0a0a0b" media="(prefers-color-scheme: dark)" />
      </Head>
      <NextThemesProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
        forcedTheme={isDarkModeRoute(router.pathname) ? undefined : 'light'}
      >
        <ApolloProvider client={createApolloClient()}>
          <NuqsAdapter>
            <AuthProvider inviteToken={router.query['inviteToken']}>
              <UserProfileProvider>
                <ThemeClassSync>
                  <ConfirmationDialogProvider>
                    <ReactHotToaster
                      position={isSmallScreen ? 'top-center' : 'bottom-left'}
                      toastOptions={{
                        style: {
                          width: isSmallScreen ? '330px' : 'auto',
                        },
                      }}
                      reverseOrder
                    />
                    <AuthConsumer>
                      {(auth) => {
                        if (!auth.isInitialized) {
                          return <SplashScreen growAnimationDuration={1000} />;
                        }

                        const isAuthenticated = auth.isAuthenticated;
                        const user = auth.user;

                        if (!isAuthenticated && !user) {
                          return (
                            <LaunchDarklyAppShell clientSideID={ldClientId} user={null}>
                              <Component {...pageProps} />
                            </LaunchDarklyAppShell>
                          );
                        }
                        if (process.env['NEXT_PUBLIC_VERCEL_ENV'] === 'production' || router.query['forceRUM']) {
                          if (!isNil(user)) {
                            datadogLogs.addLoggerGlobalContext('user_id', user.id);
                            datadogLogs.addLoggerGlobalContext(
                              'is_internal_user',
                              isEmployeeEmail(user.email) || isSavvyTestUserEmail(user.email),
                            );

                            if (!initedLibs) {
                              if (
                                router.query['forceRUM'] ||
                                (!isEmployeeEmail(user.email) && !isSavvyTestUserEmail(user.email))
                              ) {
                                const applicationId = process.env['NEXT_PUBLIC_DATADOG_RUM_APP_ID'];
                                assert(
                                  !isNil(applicationId),
                                  '.env variable NEXT_PUBLIC_DATADOG_RUM_APP_ID must be set',
                                );
                                const clientToken = process.env['NEXT_PUBLIC_DATADOG_RUM_CLIENT_TOKEN'];
                                assert(
                                  !isNil(clientToken),
                                  '.env variable NEXT_PUBLIC_DATADOG_RUM_CLIENT_TOKEN must be set',
                                );
                                // Ensure we don't initialize multiple times
                                datadogRum.init({
                                  applicationId,
                                  clientToken,
                                  site: 'datadoghq.com',
                                  service: 'savvy-pinetree-app',
                                  env: 'production',
                                  version: RUM_VERSION,
                                  sampleRate: 100,
                                  premiumSampleRate: 100,
                                  trackInteractions: true,
                                  trackFrustrations: true,
                                  defaultPrivacyLevel: 'mask-user-input',
                                  // Inject trace-context headers into first-party API calls so
                                  // RUM sessions link to the backend APM trace on the Rails service.
                                  allowedTracingUrls: [
                                    {
                                      match: 'https://api.savvywealth.com',
                                      propagatorTypes: ['datadog', 'tracecontext'],
                                    },
                                  ],
                                  // Sample 100% of browser-initiated backend traces for full
                                  // RUM->APM correlation. Independent of RUM session sampling.
                                  traceSampleRate: 100,
                                });

                                datadogRum.setUser({
                                  id: user.id?.toString(),
                                  name: `${user.firstName} ${user.lastName}`,
                                  email: user.email,
                                  login_as_session: !!loggedInAsAdvisorID(),
                                  role: user.role,
                                });
                                datadogRum.startSessionReplayRecording();
                              }
                            }
                            setInitedLibs(true);
                          }
                        }

                        return (
                          <LaunchDarklyAppShell clientSideID={ldClientId} user={user}>
                            <AdvisorDashNavProvider>{getLayout(<Component {...pageProps} />)}</AdvisorDashNavProvider>
                          </LaunchDarklyAppShell>
                        );
                      }}
                    </AuthConsumer>
                    <Toaster position="top-center" />
                  </ConfirmationDialogProvider>
                </ThemeClassSync>
              </UserProfileProvider>
            </AuthProvider>
          </NuqsAdapter>
        </ApolloProvider>
      </NextThemesProvider>
      <SpeedInsights beforeSend={(event) => (shouldReportSpeedInsightsEvent({ eventUrl: event.url }) ? event : null)} />
    </>
  );
}

export default trpc.withTRPC(App);
